/**
 * Unified Activities Inbox for Onboarding
 *
 * Top-level sub-tab showing the user's synced emails, calendar events, and
 * manually logged activities. Each activity can be assigned to an onboarding
 * plan and customer org. Replaces the per-plan activities tab.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Edit, Phone, Mail, Calendar, ListChecks, Loader2, CheckCircle,
  Trash2, ChevronDown, ChevronRight, History, Share2, Lock, Search,
  ArrowRight, Building2, ClipboardList, RefreshCw,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { apiClient } from '@/services/apiClient';
import { useAppTranslation } from '@/contexts/TranslationContext';
import type { OnboardingActivity, OnboardingPlan } from '../../../api/onboardingApi';
import * as api from '../../../api/onboardingApi';

type QuickAddType = 'note' | 'call' | 'email' | 'meeting' | 'task';

const TYPE_META: Record<string, { label: string; icon: React.ReactNode; color: string }> = {
  note:    { label: 'Note',    icon: <Edit className="h-3.5 w-3.5" />,     color: 'bg-yellow-50 text-yellow-700 hover:bg-yellow-100 border-yellow-200' },
  call:    { label: 'Call',    icon: <Phone className="h-3.5 w-3.5" />,    color: 'bg-green-50 text-green-700 hover:bg-green-100 border-green-200' },
  email:   { label: 'Email',   icon: <Mail className="h-3.5 w-3.5" />,     color: 'bg-blue-50 text-blue-700 hover:bg-blue-100 border-blue-200' },
  meeting: { label: 'Meeting', icon: <Calendar className="h-3.5 w-3.5" />, color: 'bg-purple-50 text-purple-700 hover:bg-purple-100 border-purple-200' },
  task:    { label: 'Task',    icon: <ListChecks className="h-3.5 w-3.5" />, color: 'bg-amber-50 text-amber-700 hover:bg-amber-100 border-amber-200' },
  demo:    { label: 'Demo',    icon: <Calendar className="h-3.5 w-3.5" />, color: 'bg-indigo-50 text-indigo-700' },
  stage_change: { label: 'Stage', icon: <ListChecks className="h-3.5 w-3.5" />, color: 'bg-gray-50 text-gray-600' },
};

function stripHtml(html: string | null | undefined): string {
  if (!html) return '';
  // Remove HTML tags and decode entities
  const text = html.replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&#\d+;/g, '').replace(/\s+/g, ' ').trim();
  return text.slice(0, 200);
}

function getEmailSender(act: OnboardingActivity): string {
  const meta = (act as any).metadata;
  if (typeof meta === 'string') try { return JSON.parse(meta)?.from_email || ''; } catch { return ''; }
  return meta?.from_email || '';
}

export default function ActivitiesInbox() {
  const { t } = useAppTranslation();
  const [activities, setActivities] = useState<OnboardingActivity[]>([]);
  const [plans, setPlans] = useState<OnboardingPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showAssign, setShowAssign] = useState(false);
  const [assignActivity, setAssignActivity] = useState<OnboardingActivity | null>(null);
  const [assignPlanId, setAssignPlanId] = useState('');
  const [assigning, setAssigning] = useState(false);

  // Compose
  const [composeType, setComposeType] = useState<QuickAddType | null>(null);
  const [composeTitle, setComposeTitle] = useState('');
  const [composeDesc, setComposeDesc] = useState('');
  const [composePlanId, setComposePlanId] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch user's activities via a special "all" endpoint or the existing one with no plan filter
      const [activitiesRes, plansRes] = await Promise.all([
        apiClient.get('/api/platform/v1/support/onboarding/activities/inbox'),
        api.listPlans({ status: 'active' }),
      ]);
      setActivities(activitiesRes.data?.activities || []);
      setPlans(plansRes);
    } catch (err) {
      console.error('Failed to load activities inbox', err);
      // Fallback: try loading from all active plans
      try {
        const plansRes = await api.listPlans({ status: 'active' });
        setPlans(plansRes);
        // Aggregate from plan activities
        const allActs: OnboardingActivity[] = [];
        for (const plan of plansRes.slice(0, 10)) {
          try {
            const acts = await api.listPlanActivities(plan.id);
            allActs.push(...acts);
          } catch { /* skip */ }
        }
        // Deduplicate by id
        const seen = new Set<number>();
        setActivities(allActs.filter(a => { if (seen.has(a.id)) return false; seen.add(a.id); return true; }));
      } catch { /* give up */ }
    }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = activities.filter(a => {
    if (filterType && (a.type || a.activity_type) !== filterType) return false;
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      return (a.title || a.subject || '').toLowerCase().includes(q) ||
             (a.description || '').toLowerCase().includes(q) ||
             (a.created_by_name || '').toLowerCase().includes(q);
    }
    return true;
  });

  const handleAssign = async () => {
    if (!assignActivity || !assignPlanId) return;
    setAssigning(true);
    try {
      await apiClient.patch(
        `/api/platform/v1/support/onboarding/plans/${assignPlanId}/activities/${assignActivity.id}`,
        {} // The backend will set onboarding_plan_id
      );
      // Actually we need a dedicated assign endpoint. For now, update via the activity update
      // This needs the activity to already be in the plan. Let's use a different approach:
      // Just update the onboarding_plan_id directly
      await apiClient.post(`/api/platform/v1/support/onboarding/activities/${assignActivity.id}/assign`, {
        plan_id: parseInt(assignPlanId),
      });
      setShowAssign(false);
      setAssignActivity(null);
      load();
    } catch (err: any) {
      alert('Error assigning: ' + (err.response?.data?.detail || err.message));
    }
    finally { setAssigning(false); }
  };

  const handleCompose = async () => {
    if (!composeType || !composeTitle.trim()) return;
    setSaving(true);
    try {
      if (composePlanId) {
        await api.createPlanActivity(parseInt(composePlanId), {
          activity_type: composeType,
          subject: composeTitle.trim(),
          description: composeDesc.trim() || undefined,
        });
      } else {
        // Create unassigned activity
        await apiClient.post('/api/platform/v1/support/onboarding/activities', {
          activity_type: composeType,
          subject: composeTitle.trim(),
          description: composeDesc.trim() || undefined,
        });
      }
      setComposeType(null);
      setComposeTitle('');
      setComposeDesc('');
      setComposePlanId('');
      load();
    } catch (err: any) { alert('Error: ' + (err.response?.data?.detail || err.message)); }
    finally { setSaving(false); }
  };

  const openAssign = (act: OnboardingActivity) => {
    setAssignActivity(act);
    setAssignPlanId(act.onboarding_plan_id ? String(act.onboarding_plan_id) : '');
    setShowAssign(true);
  };

  const handleToggleShare = async (act: OnboardingActivity) => {
    try {
      // Find which plan this activity belongs to, or use a generic update
      const planId = act.onboarding_plan_id || plans[0]?.id;
      if (!planId) return;
      await api.updatePlanActivity(planId, act.id, { shared_with_org: !(act.shared_with_org) } as any);
      load();
    } catch (err: any) { console.error(err); }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Activities</h2>
          <p className="text-sm text-gray-500">Your synced emails, calendar events, and logged activities. Assign to onboarding plans.</p>
        </div>
        <EmailCalendarSync onSynced={load} />
      </div>

      {/* Quick-add */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs text-gray-400">Log:</span>
        {(['note', 'call', 'email', 'meeting', 'task'] as QuickAddType[]).map(type => {
          const meta = TYPE_META[type];
          return (
            <button key={type} onClick={() => { setComposeType(composeType === type ? null : type); setComposeTitle(''); setComposeDesc(''); }}
              className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-full border transition-colors ${composeType === type ? 'ring-2 ring-blue-300 ' + meta.color : meta.color}`}>
              {meta.icon}{meta.label}
            </button>
          );
        })}
      </div>

      {/* Compose form */}
      {composeType && (
        <Card className="border-blue-200 bg-blue-50/30">
          <CardContent className="pt-4 pb-3 space-y-2">
            <div className="flex items-center gap-2">
              <Input className="flex-1 h-8 text-sm" value={composeTitle} onChange={(e) => setComposeTitle(e.target.value)} placeholder={`${TYPE_META[composeType].label} subject...`} autoFocus />
              <Select value={composePlanId} onValueChange={setComposePlanId}>
                <SelectTrigger className="w-48 h-8 text-xs"><SelectValue placeholder="Assign to plan..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="">No plan (unassigned)</SelectItem>
                  {plans.map(p => <SelectItem key={p.id} value={String(p.id)}>{p.customer_org_name || p.name || `Plan #${p.id}`}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Textarea rows={2} className="text-sm" value={composeDesc} onChange={(e) => setComposeDesc(e.target.value)} placeholder="Details..." />
            <div className="flex justify-end gap-2">
              <Button size="sm" variant="ghost" onClick={() => setComposeType(null)}>Cancel</Button>
              <Button size="sm" onClick={handleCompose} disabled={saving || !composeTitle.trim()}>
                {saving ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}Save
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-2 h-4 w-4 text-gray-400" />
          <Input className="pl-9 h-8 text-sm" placeholder="Search emails, notes, calls..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
        </div>
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-32 h-8 text-xs"><SelectValue placeholder="All types" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="">All types</SelectItem>
            {Object.entries(TYPE_META).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <span className="text-xs text-gray-400">{filtered.length} activities</span>
      </div>

      {/* Activity list */}
      {filtered.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-gray-400">
          {activities.length === 0 ? 'No activities yet. Sync emails from Microsoft 365 in Admin → Settings → Integrations, or log activities above.' : 'No activities match your filters'}
        </CardContent></Card>
      ) : (
        <div className="space-y-1">
          {filtered.map(act => {
            const meta = TYPE_META[(act.type || act.activity_type)] || TYPE_META.note;
            const isCompleted = act.completed ?? act.is_completed;
            const planName = act.onboarding_plan_id ? plans.find(p => p.id === act.onboarding_plan_id)?.customer_org_name : null;
            return (
              <div key={act.id} className="flex items-start gap-3 py-2.5 px-3 rounded-lg hover:bg-gray-50 group">
                <div className={`mt-0.5 p-1.5 rounded-full shrink-0 ${meta.color}`}>{meta.icon}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-sm font-medium ${isCompleted ? 'line-through text-gray-400' : ''}`}>{act.title || act.subject || '(No Subject)'}</span>
                    {planName && <Badge variant="outline" className="text-xs bg-blue-50 text-blue-600"><ClipboardList className="h-2.5 w-2.5 mr-0.5" />{planName}</Badge>}
                    {!act.onboarding_plan_id && <Badge variant="outline" className="text-xs text-gray-400">Unassigned</Badge>}
                  </div>
                  {act.description && <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{stripHtml(act.description)}</p>}
                  <div className="flex items-center gap-2 mt-1 text-xs text-gray-400">
                    <span>{act.activity_date ? new Date(act.activity_date).toLocaleDateString() : new Date(act.created_at).toLocaleDateString()}</span>
                    {act.created_by_name && <span>by {act.created_by_name}</span>}
                    {getEmailSender(act) && <span>from {getEmailSender(act)}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0 opacity-0 group-hover:opacity-100">
                  <Button size="sm" variant="outline" className="h-6 px-2 text-xs" onClick={() => openAssign(act)} title="Assign to plan">
                    <ArrowRight className="h-3 w-3 mr-1" />Assign
                  </Button>
                  <Button size="sm" variant="ghost" className="h-6 px-1" onClick={() => handleToggleShare(act)} title={act.shared_with_org ? 'Shared' : 'Private'}>
                    {act.shared_with_org ? <Share2 className="h-3.5 w-3.5 text-blue-500" /> : <Lock className="h-3.5 w-3.5 text-gray-300" />}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Assign to plan dialog */}
      <Dialog open={showAssign} onOpenChange={setShowAssign}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Activity to Plan</DialogTitle>
            <DialogDescription>Link this activity to a customer onboarding plan.</DialogDescription>
          </DialogHeader>
          <div className="py-3">
            {assignActivity && (
              <div className="mb-3 p-2 bg-gray-50 rounded text-sm">
                <strong>{assignActivity.title || assignActivity.subject}</strong>
                <p className="text-xs text-gray-500 mt-0.5">{assignActivity.description?.slice(0, 100)}</p>
              </div>
            )}
            <Label>Onboarding Plan</Label>
            <Select value={assignPlanId} onValueChange={setAssignPlanId}>
              <SelectTrigger className="mt-1"><SelectValue placeholder="Select a plan..." /></SelectTrigger>
              <SelectContent>
                {plans.map(p => (
                  <SelectItem key={p.id} value={String(p.id)}>
                    {p.customer_org_name || p.name || `Plan #${p.id}`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssign(false)}>Cancel</Button>
            <Button onClick={handleAssign} disabled={assigning || !assignPlanId}>
              {assigning ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" />Assigning...</> : 'Assign to Plan'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---- Email & Calendar Sync with lookback ---- */

function EmailCalendarSync({ onSynced }: { onSynced: () => void }) {
  const [syncing, setSyncing] = useState<'email' | 'calendar' | null>(null);
  const [syncResult, setSyncResult] = useState<string | null>(null);
  const [daysBack, setDaysBack] = useState('7');

  const handleEmailSync = async () => {
    setSyncing('email');
    setSyncResult(null);
    try {
      const res = await apiClient.post('/api/crm-activities/email-integrations/outlook/sync', null, {
        params: { days_back: parseInt(daysBack) || 7 },
      });
      const count = res.data?.synced_count || 0;
      setSyncResult(`Synced ${count} email${count !== 1 ? 's' : ''}`);
      onSynced();
    } catch (err: any) {
      setSyncResult(`Failed: ${err.response?.data?.detail || err.message}`);
    } finally { setSyncing(null); }
  };

  const handleCalendarSync = async () => {
    setSyncing('calendar');
    setSyncResult(null);
    try {
      const res = await apiClient.post('/api/crm-activities/calendar-sync/meetings', null, {
        params: { days_back: parseInt(daysBack) || 7, days_forward: 14 },
      });
      const count = res.data?.synced_count || res.data?.meetings_synced || 0;
      setSyncResult(`Synced ${count} meeting${count !== 1 ? 's' : ''}`);
      onSynced();
    } catch (err: any) {
      setSyncResult(`Failed: ${err.response?.data?.detail || err.message}`);
    } finally { setSyncing(null); }
  };

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <select value={daysBack} onChange={(e) => setDaysBack(e.target.value)} className="h-7 text-xs border rounded px-1.5 bg-white">
        <option value="3">3 days</option>
        <option value="7">7 days</option>
        <option value="14">14 days</option>
        <option value="30">30 days</option>
      </select>
      <Button size="sm" variant="outline" className="h-7 text-xs" onClick={handleEmailSync} disabled={syncing !== null}>
        {syncing === 'email' ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Mail className="h-3 w-3 mr-1" />}
        Sync Emails
      </Button>
      <Button size="sm" variant="outline" className="h-7 text-xs" onClick={handleCalendarSync} disabled={syncing !== null}>
        {syncing === 'calendar' ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Calendar className="h-3 w-3 mr-1" />}
        Sync Calendar
      </Button>
      {syncResult && (
        <span className={`text-xs ${syncResult.startsWith('Failed') ? 'text-red-500' : 'text-green-600'}`}>{syncResult}</span>
      )}
    </div>
  );
}
